<template>
  <van-popup
    v-model:show="show"
    position="bottom"
    round
    class="upload-drug-popup"
  >
    <div class="popup-header">
      <div class="popup-title">{{ $t('蓋章藥品單') }}</div>
      <van-icon
        name="cross"
        color="#9C9C9C"
        size="20px"
        @click="close"
        class="close-icon"
      />
    </div>

    <div class="content">

      <div class="upload-area">
        <div class="upload-box" @click="handleUpload" v-if="imageList.length < 10 && (status === 0 || status === 3 || isTempOrder)">
          <van-icon name="plus" size="24px" />
          <div class="upload-tip">{{ $t('最多10張') }}</div>
        </div>
        
        <div 
          v-for="(image, index) in imageList" 
          :key="index" 
          class="image-item"
        >
          <div class="image-wrapper" @click="previewImage(index)">
            <img :src="image" alt="" />
            <div class="preview-overlay">
              <van-icon name="eye-o" size="20px" color="#fff" />
            </div>
          </div>
          <van-icon 
            name="cross" 
            class="delete-icon" 
            @click.stop="deleteImage(index)" 
            v-if="status === 0 || status === 3 || isTempOrder"
          />
        </div>
      </div>

      <div v-if="status === 0 || status === 3 || isTempOrder" class="tip-text">
        <template v-if="status === 3">
          {{ $t('蓋章藥品單不規範，請重新上傳藥品單也可聯繫客服咨詢具體原因及情況。') }}
        </template>
        <template v-else-if="status === 0 || isTempOrder">
          {{ $t('我們會核對您所上傳的藥品單和訂單信息，請盡量將藥品單拍攝完整及清晰。') }}
        </template>
      </div>
    </div>
    
    <div class="footer">
      <van-button
        class="cancel-btn"
        plain
        size="middle"
        @click="downloadMedicineBill"
      >
        {{ $t("下載藥品單") }}
      </van-button>
      <van-button
        class="confirm-btn"
        color="#90D12E"
        size="middle"
        @click="submit"
        :disabled="(status === 0 || status === 3) && imageList.length === 0"
        v-if="status === 0 || status === 3 || isTempOrder"
      >
        {{ $t("上傳") }}
      </van-button>
    </div>
  </van-popup>
</template>

<script>
import { getMedicineBillUrl, medicineBillDownByOrderNo, medicineBillUpload } from "@/api/order.js";
import config from "@/utils/config.js";
import { ImagePreview } from 'vant';
import axios from 'axios';

export default {
  name: "UploadDrugDialog",
  data() {
    return {
      show: false,
      imageList: [],
      status: 0, // 0: 未上传, 1: 已通过, 2: 审核中, 3: 不通过需重新上传
      orderNo: '', // 订单号
      isTempOrder: false, // 是否为临时订单
      uploadFiles: [], // 保存实际文件对象，用于上传
      hasExistingImages: false // 是否已存在图片（用于判断reUpload参数）
    };
  },
  methods: {
    showDialog(status = 0, images = [], orderNo = '', isTempOrder = false) {
      console.log('showDialog called with:', { status, images, orderNo, isTempOrder });
      this.show = true;
      this.status = status;
      this.orderNo = orderNo;
      this.isTempOrder = isTempOrder;
      this.uploadFiles = []; // 清空文件列表
      this.hasExistingImages = false; // 重置已存在图片标识
      
      // 判断是否需要从接口获取图片：
      // 1. 如果是临时订单（确认订单页面），且有订单号，则获取
      // 2. 如果是普通订单，且状态不为0（未上传），且有订单号，则获取
      const shouldFetchImages = orderNo && (
        (isTempOrder && status !== 0) || 
        (status === 1 || status === 2 || status === 3)
      );
      
      if (shouldFetchImages) {
        this.fetchMedicineBillImages(orderNo);
      } else {
        // 否则使用传入的图片列表
        this.imageList = [...images];
        console.log('使用传入的图片列表:', this.imageList);
      }
    },
    
    // 下载药品单
    async downloadMedicineBill() {
      if (!this.orderNo) return;

      try {
          const loadingToast = this.$toast.loading({
        message: this.$t('加载中...'),
        forbidClick: true,
        duration: 0
      });
      setTimeout(() => {
        loadingToast.clear();
      }, 1000);
        const res = await medicineBillDownByOrderNo({ 
          orderNo: this.orderNo,
          isTempOrder: this.isTempOrder
        });
        
        if (res.resultID === 1200 && res.data) {
          // 获取下载链接
          const downloadUrl = res.data;
          
          // 创建下载链接并触发下载
          const link = document.createElement('a');
          link.href = downloadUrl;
          // 新窗口打开
          link.target = '_blank';
          link.download = downloadUrl.split('/').pop(); // 设置下载文件名
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          this.$toast(res.message);
        }
      } catch (error) {
        console.error('下载药品单失败:', error);
      }
    },
    
    // 从接口获取药品单图片
    async fetchMedicineBillImages(orderNo) {
      try {
        const res = await getMedicineBillUrl({ orderNo });
        if (res.resultID === 1200 && res.data) {
          this.imageList = res.data.urls.map(url => `${config.imgUrl}${url}`);
          // 如果获取到图片，标记为已存在图片
          if (this.imageList.length > 0) {
            this.hasExistingImages = true;
          }
          // 如果是审核不通过重新上传，需要将图片地址转换成文件，以便重新上传
          if (this.status === 3 || this.isTempOrder) {
            await this.convertUrlsToFiles(this.imageList);
          }
        } else {
          this.$toast(res.message);
        }
      } catch (error) {
        console.error('获取药品单图片失败:', error);
      }
    },
    
    // 将图片URL转换为文件对象
    async convertUrlsToFiles(imageUrls) {
      if (imageUrls.length === 0) return;
      
      const loadingToast = this.$toast.loading({
        message: this.$t('正在處理圖片...'),
        forbidClick: true,
        duration: 0
      });
      
      this.uploadFiles = []; // 清空现有文件列表
      
      try {
        for (let i = 0; i < imageUrls.length; i++) {
          try {
            const file = await this.urlToFile(imageUrls[i], `medicine_bill_${i + 1}.jpg`, 'image/png');
            this.uploadFiles.push(file);
            console.log(`成功转换图片${i + 1}:`, file);
          } catch (error) {
            console.error(`转换图片${i + 1}失败:`, error);
            // 转换失败时，在对应位置放置null，保持索引对应关系
            this.uploadFiles.push(null);
          }
        }
        
        loadingToast.clear();
        console.log('所有图片转换完成，uploadFiles:', this.uploadFiles);
        
      } catch (error) {
        loadingToast.clear();
        console.error('转换图片过程中出错:', error);
        this.$toast.fail(this.$t('處理圖片失敗'));
      }
    },
    
    // 将URL转换为File对象
    async urlToFile(url, filename, mimeType) {
      try {
        const res = await axios.get(url, { responseType: 'blob' });
        const blob = new Blob([res.data]);
        return new File([blob], filename, { type: mimeType });
      } catch (error) {
        console.error('URL转文件失败:', error);
        throw error;
      }
    },
    
    close() {
      this.show = false;
    },
    
    handleUpload() {
      // 使用文件选择器选择文件
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      
      input.onchange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
          // 限制总数最多10张
          const remainingSlots = 10 - this.imageList.length;
          const filesToProcess = Math.min(files.length, remainingSlots);
          
          for (let i = 0; i < filesToProcess; i++) {
            const file = files[i];
            // 保存文件对象用于上传
            this.uploadFiles.push(file);
            
            // 生成预览图
            const reader = new FileReader();
            reader.onload = (e) => {
              this.imageList.push(e.target.result);
            };
            reader.readAsDataURL(file);
          }
          
          if (files.length > remainingSlots) {
            this.$toast(`最多只能上传${10}张图片`);
          }
        }
      };
      
      input.click();
    },
    
    deleteImage(index) {
      this.imageList.splice(index, 1);
      // 确保uploadFiles数组长度与imageList保持一致
      if (this.uploadFiles.length > index) {
        this.uploadFiles.splice(index, 1);
      }
    },
    
    async submit() {
      // 过滤掉转换失败的文件（null值）
      const validFiles = this.uploadFiles.filter(file => file !== null);
      
      if (validFiles.length === 0) {
        this.$toast(this.$t('請選擇要上傳的圖片'));
        return;
      }
      
      const loadingToast = this.$toast.loading({
        message: this.$t('上傳中...'),
        forbidClick: true,
        duration: 0
      });
      
      try {
        // 一次性上传所有文件
        const formData = new FormData();
        formData.append('orderNo', this.orderNo); // 订单号
        // 当在确认订单页面且已存在图片时，或者状态为审核不通过时，传reUpload=1
        const shouldReUpload = this.status === 3 || (this.isTempOrder && this.hasExistingImages);
        formData.append('reUpload', shouldReUpload ? '1' : '0');
        formData.append('isTempOrder', this.isTempOrder ? '1' : '0'); // 是下单页面1，否则0
        
        // 添加所有文件到同一个FormData中
        validFiles.forEach(file => {
          formData.append('medicineBills', file); // 多个文件使用相同的字段名
        });
        
        const res = await medicineBillUpload(formData);
        
        loadingToast.clear();
        
        if (res.resultID === 1200) {
          this.$toast.success(this.$t('上傳成功'));
          // 发送上传成功事件给父组件
          setTimeout(() => { this.$emit('submit'); }, 1000);
          this.close();
        } else {
          this.$toast.fail(res.message || this.$t('上傳失敗'));
        }
      } catch (error) {
        loadingToast.clear();
        console.error('上传失败:', error);
        this.$toast.fail(this.$t('上傳失敗'));
      }
    },
    
    previewImage(index) {
      ImagePreview({
        images: this.imageList,
        startPosition: index,
        closeable: true,
        closeOnPopstate: true
      });
    }
  }
};
</script>

<style scoped lang="scss">
.upload-drug-popup {
  height: 85vh;
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 16px;
  border-bottom: 1px solid #f5f5f5;
  
  .popup-title {
    font-size: 16px;
    font-weight: 500;
    color: #323233;
  }
  
  .close-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.content {
  flex: 1;
  padding: 8px 0;
  overflow-y: auto;
  
  .upload-area {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin: 10px 16px;
    
    .upload-box {
      aspect-ratio: 1/1;
      background-color: #f8f8f8;
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      
      .upload-tip {
        margin-top: 8px;
        font-size: 12px;
        color: #999;
        text-align: center;
      }
    }
    
    .image-item {
      aspect-ratio: 1/1;
      position: relative;
      
      .image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        cursor: pointer;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
          transition: opacity 0.3s ease;
          border: 1px solid #f5f5f5;
        }
        
        .preview-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 8px;
          background-color: rgba(0, 0, 0, 0.3);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        &:hover .preview-overlay {
          opacity: 1;
        }
        
        &:active {
          .preview-overlay {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.5);
          }
        }
      }
      
      .delete-icon {
        position: absolute;
        top: 0;
        right: 0;
        background-color: rgba(0, 0, 0, 0.5);
        width: 24px;
        height: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 0 8px 0 8px;
        color: #fff;
        z-index: 10;
      }
    }
  }
  
  .tip-text {
    font-size: 13px;
    color: #FF5E50;
    background-color: #FFF4F3;
    padding: 12px;
    margin-top: 16px;
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  padding: 12px 24px 24px;
  // border-top: 1px solid #f5f5f5;
  
  .cancel-btn, .confirm-btn {
    flex: 1;
    border-radius: 8px;
    height: 40px;
    line-height: 40px;
  }
  
  .cancel-btn {
    margin-right: 12px;
    border-color: #90D12E;
    color: #90D12E;
  }
}
</style> 